// File: nodes/CustomerProfileNode.jsx
import React from 'react';
import { Handle, Position } from 'reactflow';
import {
  Box,
  Flex,
  Text,
  IconButton,
  HStack,
  VStack,
  Tag,
} from '@chakra-ui/react';
import {
  FaUserCog,
  FaCheckCircle,
  FaTimesCircle,
  FaDatabase
} from 'react-icons/fa';
import { FiCopy, FiEdit, FiTrash2 } from 'react-icons/fi';

const CustomerProfileNode = ({ data }) => {
  // Support both multi-condition array and legacy single-field shape
  const conditions =
    data.conditions && data.conditions.length > 0
      ? data.conditions
      : data.profileKey
      ? [
          {
            profile_key: data.profileKey,
            operator: data.operator || 'equals',
            match_value: data.matchValue || '',
          },
        ]
      : [];

  const operatorSymbol = (op) => {
    const map = {
      equals: '=',
      not_equals: '≠',
      contains: '⊃',
      starts_with: '↦',
      ends_with: '↤',
      greater_than: '>',
      less_than: '<',
      is_empty: 'ø',
      is_not_empty: '¬ø',
    };
    return map[op] || op;
  };

  const firstCondition = conditions[0];
  const extraCount = conditions.length - 1;

  return (
    <Box
      bg="white"
      border="2px solid"
      borderColor="#FBAF3F"
      borderRadius="xl"
      boxShadow={data.selected ? 'xl' : 'lg'}
      minW="240px"
      position="relative"
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ width: 12, height: 12, background: '#FBAF3F' }}
      />

      {/* Header */}
      <Flex
        bg="#FBAF3F"
        color="white"
        px={4}
        py={2.5}
        borderTopRadius="xl"
        align="center"
        gap={2}
      >
        <FaUserCog size={16} />
        <Text noOfLines={1} flex={1} fontSize="sm" fontWeight="medium">
          Customer Profile
        </Text>
        <HStack spacing={1}>
          <IconButton
            icon={<FiEdit size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => {
              e.stopPropagation();
              data.onEdit?.(data.id);
            }}
            aria-label="Edit"
          />
          <IconButton
            icon={<FiCopy size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => {
              e.stopPropagation();
              data.onDuplicate?.(data.id);
            }}
            aria-label="Duplicate"
          />
          <IconButton
            icon={<FiTrash2 size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => {
              e.stopPropagation();
              data.onDelete?.(data.id);
            }}
            aria-label="Delete"
          />
        </HStack>
      </Flex>

      {/* Body */}
      <Box p={3}>
        <Text fontSize="sm" fontWeight="medium" mb={3}>
          {data.label || 'Check Customer Profile'}
        </Text>

        {/* Conditions preview */}
        <Box
          bg="blue.50"
          border="1px solid"
          borderColor="blue.200"
          borderRadius="lg"
          p={2.5}
          mb={3}
        >
          <Flex align="flex-start" gap={2}>
            <Box color="blue.600" mt={0.5} flexShrink={0}>
              <FaDatabase size={14} />
            </Box>
            <Box flex={1} minW={0}>
              <Text fontSize="10px" fontWeight="semibold" color="blue.900" mb={1}>
                {conditions.length === 0
                  ? 'Not configured'
                  : 'Profile Conditions (AND)'}
              </Text>

              {firstCondition && (
                <Flex align="center" gap={1.5} flexWrap="wrap">
                  <Tag
                    fontSize="10px"
                    colorScheme="blue"
                    variant="subtle"
                    px={2}
                    py={0.5}
                    borderRadius="sm"
                    border="1px solid"
                    borderColor="blue.300"
                    textTransform="none"
                  >
                    {firstCondition.profile_key || '—'}
                  </Tag>
                  <Text fontSize="10px" color="blue.600" fontWeight="bold">
                    {operatorSymbol(firstCondition.operator)}
                  </Text>
                  {firstCondition.operator !== 'is_empty' &&
                    firstCondition.operator !== 'is_not_empty' && (
                      <Text
                        fontSize="10px"
                        color="blue.800"
                        fontFamily="mono"
                        maxW="80px"
                        isTruncated
                      >
                        {firstCondition.match_value || '(empty)'}
                      </Text>
                    )}
                </Flex>
              )}

              {extraCount > 0 && (
                <Text fontSize="10px" color="blue.600" mt={1} fontStyle="italic">
                  +{extraCount} more condition{extraCount > 1 ? 's' : ''}
                </Text>
              )}
            </Box>
          </Flex>
        </Box>

        {/* Branches */}
        <VStack spacing={2} align="stretch">
          {/* True Branch */}
          <Box
            position="relative"
            border="1px solid"
            borderColor="green.200"
            borderRadius="lg"
            p={2}
            bg="green.50"
          >
            <Flex align="center" gap={2}>
              <Box color="green.600" flexShrink={0}>
                <FaCheckCircle size={13} />
              </Box>
              <Text fontSize="xs" fontWeight="medium" color="green.700">
                {data.trueLabel || 'Match Found (True)'}
              </Text>
            </Flex>
            <Handle
              type="source"
              position={Position.Right}
              id="true"
              className="branch-handle"
              style={{
                width: 10,
                height: 10,
                background: '#22c55e',
                border: '2px solid white',
                top: '50%',
                transform: 'translateY(-50%)',
                '--flow-branch-offset': '-20px',
              }}
            />
          </Box>

          {/* False Branch */}
          <Box
            position="relative"
            border="1px solid"
            borderColor="red.200"
            borderRadius="lg"
            p={2}
            bg="red.50"
          >
            <Flex align="center" gap={2}>
              <Box color="red.500" flexShrink={0}>
                <FaTimesCircle size={13} />
              </Box>
              <Text fontSize="xs" fontWeight="medium" color="red.700">
                {data.falseLabel || 'No Match (False)'}
              </Text>
            </Flex>
            <Handle
              type="source"
              position={Position.Right}
              id="false"
              className="branch-handle"
              style={{
                width: 10,
                height: 10,
                background: '#ef4444',
                border: '2px solid white',
                top: '50%',
                transform: 'translateY(-50%)',
                '--flow-branch-offset': '-20px',
              }}
            />
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default CustomerProfileNode;